// ─── Project-scoped access storage ───────────────────────────────────────────
//
// Where this lives and why:
//
//   organization/{orgId}/projectAccess/{userId}
//     { userId, access: { [projectId]: true }, updatedAt }
//
// The obvious place would have been the `members[]` array on the organization
// doc (next to assignedProjects/assignedChannels), but that array is already the
// single biggest growth risk in this schema: every member's profile, project
// list and channel map share ONE document's 1MB budget, so adding a per-project
// role map for every member would bring that ceiling much closer.
//
// Keying by user instead:
//   • The logged-in user resolves every project role they hold in ONE doc read
//     (the hot path — it runs on every Dashboard load).
//   • A doc grows only with the number of projects, and an entry costs roughly
//     45 bytes, so a single user would need ~23,000 projects to approach 1MB.
//   • Nothing is written back into members[], so the org doc stops growing.
//
// The per-project admin UI reads the whole `projectAccess` collection (one
// query, one doc per user) and writes only the users whose editor access
// actually changed, using dotted field paths so concurrent edits to *other*
// projects in the same user doc cannot clobber each other.

import {
  collection,
  deleteField,
  doc,
  getDoc,
  getDocs,
  serverTimestamp,
  writeBatch,
} from "firebase/firestore";
import { db } from "../firebase/firebase";

/** projectId → true (editor access), for a single user. */
export type ProjectAccessMap = Record<string, boolean>;

/** userId → (projectId → true), for a whole organization. */
export type OrgProjectAccess = Record<string, ProjectAccessMap>;

/** Normalises whatever is stored into a clean boolean access map. */
export const normalizeAccessMap = (raw: any): ProjectAccessMap => {
  if (!raw || typeof raw !== "object") return {};
  return Object.entries(raw).reduce((acc, [projectId, value]) => {
    if (value === true || value === "true") {
      acc[projectId] = true;
    }
    return acc;
  }, {} as ProjectAccessMap);
};

const accessDocRef = (organizationId: string, userId: string) =>
  doc(db, "organization", organizationId, "projectAccess", userId);

/**
 * Every project role held by one user. This is the read the app makes on
 * login — a single document, regardless of how many projects exist.
 */
export const fetchUserProjectAccess = async (
  organizationId: string,
  userId: string
): Promise<ProjectAccessMap> => {
  if (!organizationId || !userId) return {};
  try {
    const snap = await getDoc(accessDocRef(organizationId, userId));
    if (!snap.exists()) return {};
    return normalizeAccessMap(snap.data()?.access);
  } catch (error) {
    console.error("Error fetching project access for user:", error);
    return {};
  }
};

/**
 * Every project role in the organization, keyed by user. Used by the
 * per-project admin modal, which needs to show a level for each member.
 */
export const fetchOrgProjectAccess = async (
  organizationId: string
): Promise<OrgProjectAccess> => {
  if (!organizationId) return {};
  try {
    const snap = await getDocs(
      collection(db, "organization", organizationId, "projectAccess")
    );
    return snap.docs.reduce((acc, docSnap) => {
      acc[docSnap.id] = normalizeAccessMap(docSnap.data()?.access);
      return acc;
    }, {} as OrgProjectAccess);
  } catch (error) {
    console.error("Error fetching organization project access:", error);
    return {};
  }
};

/**
 * Persist editor access for ONE project across several users.
 * `editorByUser` maps userId → true/false.
 * Only pass the users whose access actually changed.
 */
export const saveProjectAccess = async (
  organizationId: string,
  projectId: string,
  editorByUser: Record<string, boolean>
): Promise<void> => {
  const entries = Object.entries(editorByUser);
  if (!organizationId || !projectId || entries.length === 0) return;

  const batch = writeBatch(db);

  entries.forEach(([userId, hasEditorAccess]) => {
    const ref = accessDocRef(organizationId, userId);

    if (hasEditorAccess) {
      // Nested-map merge: touches only this project's key, leaving every other
      // project the user has access to untouched.
      batch.set(
        ref,
        { userId, access: { [projectId]: true }, updatedAt: serverTimestamp() },
        { merge: true }
      );
    } else {
      // set-then-update in the same batch: the set guarantees the document
      // exists so the field delete below cannot fail on a missing doc.
      batch.set(ref, { userId, updatedAt: serverTimestamp() }, { merge: true });
      batch.update(ref, { [`access.${projectId}`]: deleteField() });
    }
  });

  await batch.commit();
};

/**
 * Drop a project from every user's access map — called when a project is
 * deleted so revoked ids don't linger.
 */
export const removeProjectFromAllAccess = async (
  organizationId: string,
  projectId: string
): Promise<void> => {
  if (!organizationId || !projectId) return;
  try {
    const snap = await getDocs(
      collection(db, "organization", organizationId, "projectAccess")
    );
    const affected = snap.docs.filter(
      (d) => normalizeAccessMap(d.data()?.access)[projectId]
    );
    if (affected.length === 0) return;

    const batch = writeBatch(db);
    affected.forEach((d) => {
      batch.update(d.ref, {
        [`access.${projectId}`]: deleteField(),
        updatedAt: serverTimestamp(),
      });
    });
    await batch.commit();
  } catch (error) {
    console.error("Error clearing project access for deleted project:", error);
  }
};
